import { useEffect, useMemo, useState } from 'react';
import { Card, Col, Divider, Empty, Form, FormValues, Progress, Row, Tooltip, Typography } from 'antd';
import { useLazyGetSignedDocumentsByUserQuery } from '../../api/dashboard-api';
import styles from './signed-documents-by-user.module.css';
import { Trans, useTranslation } from 'react-i18next';
import FormActions from '../../../../shared/components/form-actions/form-actions';
import { SortAscendingOutlined, SortDescendingOutlined } from '@ant-design/icons';
import { SignDocumentsFilterFormModel } from '../../models/sign-documents-filter-form-model';
import { signDocumentsByUserFilterFormId } from '../../constants/constants';
import { mapDateSearchParams } from '../../../sign-requests/utils/map-date-search-params';
import CustomButton from '../../../../shared/components/buttons/custom-button/custom-button';
import SignDocumentsFilterForm from '../sign-documents-filter-form/sign-documents-filter-form';
import { startOfMonth } from 'date-fns';

const SignedDocumentsByUser = (): JSX.Element => {
  const [getSignedDocumentsByUser, { data: signedDocuments }] = useLazyGetSignedDocumentsByUserQuery();
  const { t } = useTranslation('dashboard');
  const [ascending, setAscending] = useState(false);
  const [form] = Form.useForm<SignDocumentsFilterFormModel>();

  const firstOfMonth = startOfMonth(new Date());
  const initialRange = useMemo(() => [firstOfMonth, new Date()], [firstOfMonth]);

  useEffect(() => {
    form.setFieldsValue({ dateFromTo: initialRange });
    getSignedDocumentsByUser(mapDateSearchParams(initialRange.toString()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const maxCount = useMemo(
    () => Math.max(0, ...(signedDocuments || []).map(document => Number(document.count))),
    [signedDocuments],
  );

  const totalSum = useMemo(() => (signedDocuments || []).reduce((a, b) => a + Number(b.count), 0), [signedDocuments]);

  const sortedDocs = useMemo(() => {
    const docs = [...(signedDocuments || [])];
    docs.sort((a, b) => (ascending ? Number(a.count) - Number(b.count) : Number(b.count) - Number(a.count)));
    return docs;
  }, [signedDocuments, ascending]);

  const filterRequestsByUser = (formData: FormValues<typeof form>) => {
    const dateIntervalString = formData.dateFromTo?.toString();
    const payload = {
      unit: formData.unit,
      ...mapDateSearchParams(dateIntervalString || ''),
    };

    getSignedDocumentsByUser(payload);
  };

  return (
    <Card
      title={t('signedDocumentByUser.title')}
      extra={<Trans i18nKey="label.totalSigned" t={t} ns="dashboard" values={{ count: totalSum }} />}
      className="dashboard-card"
    >
      <Row gutter={16} align="middle">
        <Col flex="auto">
          <SignDocumentsFilterForm
            form={form}
            onFinish={filterRequestsByUser}
            formId={signDocumentsByUserFilterFormId}
            hasUnitSelect={true}
          />
        </Col>
        <Col flex="auto">
          <Row gutter={16} justify="space-between">
            <Col flex="auto">
              <FormActions
                form={form}
                formId={signDocumentsByUserFilterFormId}
                clearFilters={() => getSignedDocumentsByUser()}
              />
            </Col>
            <Col>
              <CustomButton
                className={styles.sortIconWrapper}
                type="textBg"
                onClick={() => setAscending(prev => !prev)}
              >
                {!ascending ? <SortAscendingOutlined /> : <SortDescendingOutlined />}
              </CustomButton>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col span={12} className="text-semi-bold">
          {t('label.user')}
        </Col>
        <Col span={12} className="text-semi-bold">
          {t('label.signatureCount')}
        </Col>
      </Row>
      <Divider className={styles.divider} />
      <div className={styles.dataWrapper}>
        {sortedDocs.length > 0 ? (
          sortedDocs?.map(item => {
            const percent = Number(item.count) > 0 ? (Number(item.count) * 100) / maxCount : 0;

            return (
              <div key={item.id} className={styles.row}>
                <Row className={styles.rowHeader} align="middle">
                  <Col span={12}>
                    <Tooltip title={item.name}>
                      <Typography.Text ellipsis={true}>{item.name}</Typography.Text>
                    </Tooltip>
                  </Col>
                  <Col span={12} className={styles.count}>
                    {item.count}
                  </Col>
                </Row>
                <Progress percent={percent} size="small" strokeColor={'#47B731'} showInfo={false} />
              </div>
            );
          })
        ) : (
          <Empty />
        )}
      </div>
    </Card>
  );
};

export default SignedDocumentsByUser;
